// @flow
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Trans } from "react-i18next";
import { Form, Divider, Container, Button, Icon, Grid, Segment } from "semantic-ui-react";
import { reduxForm, Field, type FormProps, formValueSelector, change as changeAction } from "redux-form";
import { Link } from "react-router-dom";

import { IconOptions } from "modules/ChangeService/Wizard/misc/components/IconOptions";
import { getStepRoute } from "modules/ChangeService/Wizard/misc/Routes";
import { SWITCH_PROVIDER } from "modules/ChangeService/Wizard/misc/Constants";
import { HOUSE, APT, COMPANY } from "Commons/Utilities/Constants/PropertyTypes";
import { InputField } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/components/Fields";
import { Loading } from "Commons/Components";
import type { CategoryT } from "Commons/Modules/category";
import type { PathT, StepT } from "modules/ChangeService/Wizard/misc/Types";

import { ReasonForChange } from "modules/ChangeService/Wizard/misc/components/ReasonForChange";
import Consumption from "modules/ChangeService/Wizard/misc/components/Consumption";
import { useChangeServiceHook } from "modules/ChangeService/Wizard/misc/ChangeServiceHook";
import { isRequired } from "modules/ChangeService/ChangeServices/submodules/Old_Edit/misc/Validators";
import { isNumberSmallerThan, isNumber, isNumberGreaterThan, isNumberWithoutDots } from "Commons/Utilities/validators";
import { maxConsumptionGas, maxConsumptionStrom } from "Commons/Utilities/Constants/MaxConsumption";
import { FormRow } from "../FormRow";
import { FormLabelWithDivider as FormLabel } from "Commons/Components/FormComponents/components/FormLabelWithDivider";
import HighConsumption from "../../../misc/components/HighConsumption/HighConsumption";

type Props = {|
  onNavigation: Function,
  steps: PathT,
  step: StepT,
  authenticated: boolean,
  categorySlug: string,
  category: CategoryT,
|} & FormProps;

const consumptions = {
  [HOUSE]: { STROM: 2500, GAS: 12000 },
  [APT]: { STROM: 1500, GAS: 5000 },
  [COMPANY]: { STROM: 12000, GAS: 50000 },
};

const selector = formValueSelector("ChangeService");

function ContractStep(props: Props) {
  const { onNavigation, steps, step, authenticated, categorySlug, change, location } = props;
  const {
    nextStep,
    t,
    currentValues: { switchProviderOrMove, propertyType },
    isCategoryStrom,
    category,
  } = useChangeServiceHook({
    steps,
    step,
    categorySlug,
    change,
  });

  const dispatch = useDispatch();

  const consumptionFieldName = switchProviderOrMove === SWITCH_PROVIDER ? "currentConsumption" : "expectedConsumption";
  const hasHighConsumption = useSelector(
    (state) => selector(state, consumptionFieldName) >= (isCategoryStrom ? 10000 : 200000)
  );

  const identifiesAsPrivateCustomer = useSelector((state) => selector(state, "hasHighConsumption"));
  const isCompany = propertyType === COMPANY;

  useEffect(() => {
    if (!hasHighConsumption) {
      const consumption = consumptions[propertyType][isCategoryStrom ? "STROM" : "GAS"];
      dispatch(changeAction("ChangeService", consumptionFieldName, consumption));
    }
  }, [propertyType]);

  const urlParams = new URLSearchParams(window.location.search);
  const objectType = urlParams.get("objectType");
  // Set business type if query params exist in url
  useEffect(() => {
    if (objectType === "company") {
      dispatch(changeAction("ChangeService", "propertyType", "COMPANY"));
    }
  }, []);

  useEffect(() => {
    if (!isCompany && identifiesAsPrivateCustomer === "no") {
      dispatch(changeAction("ChangeService", "propertyType", "COMPANY"));
      dispatch(changeAction("ChangeService", "hasHighConsumption", "yes"));
    }
  }, [isCompany, identifiesAsPrivateCustomer, dispatch, change]);

  if (!category) return <Loading />;

  const maxConsumption = isCategoryStrom ? maxConsumptionStrom : maxConsumptionGas;

  return (
    <Form>
      <Grid stackable>
        <FormRow
          titleComponent={
            <FormLabel
              desktop
              width={16}
              title={t("wizard.contract.reasonForChange.label")}
              centered
              inline
              tooltip={t("wizard.contract.reasonForChange.info")}
              secondary
            />
          }
        >
          <Form.Group>
            <ReasonForChange t={t} change={change} />
          </Form.Group>
        </FormRow>
        <FormRow
          titleComponent={
            <FormLabel
              desktop
              width={16}
              title={t("wizard.customer.propertyType.label")}
              centered
              inline
              tooltip={t("wizard.customer.propertyType.info")}
              secondary
            />
          }
        >
          <Field
            name="propertyType"
            component={IconOptions}
            props={{
              options: [
                {
                  icon: "house",
                  label: t("wizard.customer.propertyType.options.HOUSE"),
                  value: HOUSE,
                  dataCy: HOUSE,
                },
                {
                  icon: "company",
                  label: t("wizard.customer.propertyType.options.APT"),
                  value: APT,
                  dataCy: APT,
                },
                {
                  icon: "industry",
                  label: t("wizard.customer.propertyType.options.COMPANY"),
                  value: COMPANY,
                  dataCy: COMPANY,
                },
              ],
            }}
          />
        </FormRow>
        <FormRow
          titleComponent={
            <FormLabel
              desktop
              divider={false}
              width={16}
              title={t("wizard.contract.consumption.label")}
              centered
              inline
              tooltip={t("wizard.contract.consumption.info")}
              secondary
            />
          }
        >
          <>
            <Consumption
              consumptionFieldName={consumptionFieldName}
              isCompany={isCompany}
              isCategoryStrom={isCategoryStrom}
            />
          </>
        </FormRow>
        <FormRow>
          <Field
            name={consumptionFieldName}
            component={InputField}
            validate={[
              isRequired,
              isNumber,
              isNumberSmallerThan(maxConsumption),
              isNumberGreaterThan(0),
              isNumberWithoutDots,
            ]}
            props={{
              placeholder: t("mobile.offer.consumption.defaultText"),
              width: 16,
              labelPosition: "right",
              label: { basic: true, content: "kWh" },
              role: "textbox",
            }}
          />
        </FormRow>
        <FormRow>
          {!isCompany && hasHighConsumption && (
            <Segment color="yellow">
              <HighConsumption isCategoryStrom={isCategoryStrom} />
            </Segment>
          )}
        </FormRow>
        <FormRow
          titleComponent={
            <FormLabel
              desktop
              width={16}
              divider={false}
              title={
                <Trans i18nKey={"changeService:desktop.offer.ecoTariff.label"}>
                  1<br />2
                </Trans>
              }
              centered
              inline
              tooltip={t("wizard.contract.ecoTariff.info")}
              secondary
            />
          }
        >
          <Field
            name="ecoEnergy"
            component={IconOptions}
            props={{
              options: [
                {
                  icon: "sun",
                  label: t("mobile.offer.ecoTariff.options.YES"),
                  value: true,
                  dataCy: "ecoEnergyTrue",
                },
                {
                  icon: "world",
                  label: t("mobile.offer.ecoTariff.options.NO"),
                  value: false,
                  dataCy: "ecoEnergyFalse",
                },
              ],
            }}
          />
        </FormRow>
        <Divider hidden />
        <Grid.Row width={16}>
          <Container textAlign="center">
            <Link
              to={{
                pathname: getStepRoute(authenticated, categorySlug, nextStep.route),
                search: location.search,
              }}
            >
              <Button data-cy={"NextStep"} primary size="big" onClick={onNavigation}>
                {t("common:next")}
                <Icon name="arrow right" />
              </Button>
            </Link>
          </Container>
        </Grid.Row>
      </Grid>
    </Form>
  );
}

const Contract = reduxForm({
  form: "ChangeService",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(ContractStep);

export { Contract };
